import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'
import config from '../config'
import moment from 'moment'

Vue.use(Vuex)

const defaultUserInfo = {
  skills: [],
  interests: [],
  languages: [],
  communities: []
}
const initialState = {
  /**
   * Current connected user
   */
  user: {
    // prevent flickering when loading the app, data will be erased after currentUser load
    securityRemindedAt: moment.utc()
  },
  /**
   * Communities of the connected user
   */
  userCommunities: [],
  /**
   * Any user informations
   */
  userInfo: { ...defaultUserInfo },
  /**
   * User stats
   */
  userStats: {},
  reportedContentCount: null
}

const getters = {
  user: state => state.user,
  userCommunities: state => state.userCommunities,
  userInfo: state => state.userInfo,
  userStats: state => state.userStats,
  reportedContentCount: state => state.reportedContentCount
}

const mutations = {
  user: (state, user) => {
    state.user = user
  },
  userCommunities: (state, userCommunities) => {
    state.userCommunities = userCommunities
  },
  userInfo: (state, userInfo) => {
    // defaultUserInfo ensure all fields are defined
    // state.userInfo ensure previous fields are kept if new data doesn't provide them
    state.userInfo = { ...defaultUserInfo, ...state.userInfo, ...userInfo }
  },
  userStats: (state, userStats) => {
    state.userStats = userStats
  },
  addUserSkills: (state, userSkills) => {
    state.userInfo.skills.push(userSkills)
  },
  removeUserSkills: (state, id) => {
    const i = state.userInfo.skills.map(item => item.id).indexOf(id)
    state.userInfo.skills.splice(i, 1)
  },
  updateUserInterests: (state, userInterests) => {
    state.userInfo.interests = userInterests.interests.slice()
  },
  addUserInterests: (state, userInterests) => {
    state.userInfo.interests.push(userInterests)
  },
  removeUserInterests: (state, id) => {
    const i = state.userInfo.interests.map(item => item.id).indexOf(id)
    state.userInfo.interests.splice(i, 1)
  },
  updateUserLanguages: (state, userLanguages) => {
    state.userInfo.languages = userLanguages.languages.slice()
  },
  addUserLanguages: (state, newLanguage) => {
    if (!state.userInfo.languages) {
      Vue.set(state.userInfo, 'languages', newLanguage)
    } else {
      state.userInfo.languages.push(newLanguage)
    }
  },
  removeUserLanguages: (state, id) => {
    const i = state.userInfo.languages.map(item => item.id).indexOf(id)
    state.userInfo.languages.splice(i, 1)
  },
  setUserAgreement: (state) => {
    state.user.agreement = true
  },
  setUserSecurityReminded: (state, date) => {
    state.user.securityRemindedAt = date
  },
  countNewNotifications: (state, newNotificationsNumber) => {
    state.user.newNotificationsNumber = newNotificationsNumber
  },
  countReportedContent: (state, reportedContentCount) => {
    state.reportedContentCount = reportedContentCount
  },
  decrementCountReportedContent: (state) => {
    state.reportedContentCount = state.reportedContentCount - 1
  },
  decrementCountPublishedPosts: (state) => {
    state.userStats.countPublishedPosts = state.userStats.countPublishedPosts - 1
  },
  incrementCountPublishedPosts: (state) => {
    state.userStats.countPublishedPosts = state.userStats.countPublishedPosts + 1
  }
}

const actions = {
  fetchUserById({ commit }, userId) {
    return axios.get(`${config.getRootApi()}/users/${userId}`)
      .then(response => {
        commit('userInfo', response.data)
      })
  },
  async fetchUserCommunities({ commit }) {
    return axios.get(`${config.getRootApi()}/communities/current`)
      .then(response => {
        commit('userCommunities', response.data)
      })
  },
  async fetchCurrentUser({ rootState, commit }) {
    const auth = rootState.security.auth
    return axios.get(`${config.getRootApi()}/users/${auth.id}`)
      .then(response => {
        commit('user', response.data)
        commit('userCommunities', response.data.communities)
      })
  },
  fetchUserStats({ commit }) {
    return axios.get(`${config.getRootApi()}/me/stats`)
      .then(response => {
        commit('userStats', response.data)
      })
  },
  updateMyProfile({ commit, dispatch }, formData) {
    return axios.patch(`${config.getRootApi()}/me/update`, formData, {
      headers: {
        'content-type': 'multipart/form-data'
      }
    })
      .then(response => {
        commit('userInfo', response.data)
        dispatch('refreshImage', response.data.idImage)
      })
  },
  countNewNotifications({ commit }) {
    return axios.get(`${config.getRootApi()}/me/countNewNotifications`)
      .then(response => {
        commit('countNewNotifications', response.data.newNotificationsNumber)
        commit('countReportedContent', response.data.reportedContentCount)
      })
  },
  async seeNewNotifications({ commit }) {
    const response = await axios.post(`${config.getRootApi()}/me/newNotifications`, null)
    if (response.status === 200) {
      commit('countNewNotifications', 0)
    }
  },
  async addSkill({ commit }, skill) {
    const response = await axios.post(`${config.getRootApi()}/me/skills/${skill.id}`, null)
    if (response.status === 200) {
      commit('addUserSkills', skill)
    }
  },
  async removeSkill({ commit }, skillId) {
    const response = await axios.delete(`${config.getRootApi()}/me/skills/${skillId}`)
    if (response.status === 200) {
      commit('removeUserSkills', skillId)
    }
  },
  async updateInterests({ commit }, interestsData) {
    const res = await axios.post(`${config.getRootApi()}/me/interests/update`, interestsData)
    if (res.status === 200) {
      commit('updateUserInterests', interestsData)
    } else {
      console.error(res)
    }
  },
  async addInterest({ commit }, interest) {
    const response = await axios.post(`${config.getRootApi()}/me/interests/${interest.id}`, null)
    if (response.status === 200) {
      commit('addUserInterests', interest)
    }
  },
  async removeInterest({ commit }, interestId) {
    const response = await axios.delete(`${config.getRootApi()}/me/interests/${interestId}`)
    if (response.status === 200) {
      commit('removeUserInterests', interestId)
    }
  },
  async updateLanguages({ commit }, languagesData) {
    const res = await axios.post(`${config.getRootApi()}/me/languages/update`, languagesData)
    if (res.status === 200) {
      commit('updateUserLanguages', languagesData)
    } else {
      console.error(res)
    }
  },
  async addLanguage({ commit, state }, newLanguage) {
    const response = await axios.post(`${config.getRootApi()}/me/languages/${newLanguage.id}`, null)
    if (response.status === 200) {
      commit('addUserLanguages', newLanguage)
    }
  },
  async removeLanguage({ commit, state }, languageId) {
    const response = await axios.delete(`${config.getRootApi()}/me/languages/${languageId}`)
    if (response.status === 200) {
      commit('removeUserLanguages', languageId)
    }
  },
  async setUserAgreement({ commit }) {
    const response = await axios.put(`${config.getRootApi()}/me/agree`, null)
    if (response.status === 200) {
      commit('setUserAgreement')
    }
  },
  async setUserSecurityReminded({ commit }) {
    const response = await axios.put(`${config.getRootApi()}/me/securityReminded`, null)
    if (response.status === 200) {
      commit('setUserSecurityReminded', response.data.securityRemindedAt)
    }
  }
}

export default {
  state: initialState,
  getters,
  mutations,
  actions
}
